import {
  createContext, useContext, useCallback, useState
} from 'react';

import useLocalStorage from '@zenbusiness/application-commons-hooks/useLocalStorage';
import useMemoObject from '@zenbusiness/application-commons-hooks/useMemoObject';

export const LS_VISIT_UUID = 'visitUUID';
export const LS_VISIT_PROCESSED = 'visitProcessed';

const VisitContext = createContext();

/**
 * Hook to access session (a.k.a. Visit) information
 *
 * @returns {object} visitUuid
 */
const useVisit = () => {
  const context = useContext(VisitContext);

  if (typeof context === 'undefined') {
    throw new Error('useVisit must be used within a VisitContextProvider');
  }

  const visit = context;

  return visit;
};

export default useVisit;

export function VisitProvider({ children }) {
  const [visitUuid, setVisitUuid] = useLocalStorage(LS_VISIT_UUID, null, { isJSON: false });
  const [visitProcessed, setVisitProcessed] = useLocalStorage(LS_VISIT_PROCESSED, null, {
    isJSON: true
  });

  const setVisit = useCallback(({ visitUuid: vu, visitProcessed: vp }) => {
    setVisitUuid(vu);
    setVisitProcessed(vp);
  }, [setVisitUuid, setVisitProcessed]);

  const visit = useMemoObject({ visitUuid, visitProcessed });

  const cleanVisit = useCallback(() => {
    setVisitUuid();
    setVisitProcessed();
  }, [setVisitUuid, setVisitProcessed]);

  const context = useMemoObject({ visit, setVisit, cleanVisit });

  return (
    <VisitContext.Provider value={context}>{children}</VisitContext.Provider>
  );
}

/* eslint-disable react/prop-types */
export function MockVisitProvider({
  initialVisitUUID,
  initialVisitProcessed,
  onVisitChange,
  children
}) {
  const [visitUuid, setVisitUuid] = useState(initialVisitUUID);
  const [visitProcessed, setVisitProcessed] = useState(initialVisitProcessed);

  const setVisit = useCallback(({ visitUuid: vu, visitProcessed: vp }) => {
    setVisitUuid(vu);
    setVisitProcessed(vp);
    onVisitChange({ visitUuid: vu, visitProcessed: vp });
  }, [onVisitChange, setVisitUuid, setVisitProcessed]);

  const visit = useMemoObject({ visitUuid, visitProcessed });

  const cleanVisit = useCallback(() => {
    setVisitUuid();
    setVisitProcessed();
    onVisitChange({ visitUuid: null, visitProcessed: null });
  }, [onVisitChange, setVisitUuid, setVisitProcessed]);

  const context = useMemoObject({ visit, setVisit, cleanVisit });

  return (
    <VisitContext.Provider value={context}>{children}</VisitContext.Provider>
  );
}

class VisitStorage {
  static get visitUUID() {
    return window.localStorage.getItem(LS_VISIT_UUID);
  }

  static set visitUUID(value) {
    if (value === null || value === undefined) {
      window.localStorage.removeItem(LS_VISIT_UUID);
    } else {
      window.localStorage.setItem(LS_VISIT_UUID, value);
    }
  }

  static get visitProcessed() {
    return window.localStorage.getItem(LS_VISIT_PROCESSED);
  }

  static set visitProcessed(value) {
    if (value === null || value === undefined) {
      window.localStorage.removeItem(LS_VISIT_PROCESSED);
    } else {
      window.localStorage.setItem(LS_VISIT_PROCESSED, value);
    }
  }

  static clean() {
    window.localStorage.removeItem(LS_VISIT_UUID);
    window.localStorage.removeItem(LS_VISIT_PROCESSED);
  }
}

export {
  VisitStorage as UNSAFE_VisitStorage // eslint-disable-line camelcase
};
